/**
 * Sign up and pay.
 *
 * The form collects what a venue is, then hands the browser to Stripe. No card
 * field appears anywhere in this file, or anywhere in this application --
 * Checkout is hosted by Stripe, which is the difference between being in PCI
 * scope and not.
 *
 * The address is checked while they type, because it ends up printed on QR
 * codes bolted to posts and "that one is taken" is a sentence somebody should
 * read before paying rather than after.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Footer, Nav } from "./Shell";
import "./site.css";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$49.99",
    who: "One course, one screen.",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$99.99",
    who: "Several courses, several screens.",
  },
];

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);

/** The browser's own guess, so most venues never touch the field. */
const guessZone = () => {
  if (typeof window === "undefined") return "UTC";
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

type Availability = { checking: boolean; ok: boolean | null; reason: string | null };

export function Signup() {
  // Guarded because this component is also rendered at build time, where there
  // is no window. The prerendered copy shows the default plan and the browser
  // corrects it on mount from ?plan=.
  const params = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const [plan, setPlan] = useState(
    PLANS.some((p) => p.id === params.get("plan")) ? params.get("plan")! : "starter",
  );
  const [venueName, setVenueName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [timezone] = useState(guessZone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability>({
    checking: false,
    ok: null,
    reason: null,
  });

  const effectiveSlug = useMemo(
    () => (slugTouched ? slug : slugify(venueName)),
    [slug, slugTouched, venueName],
  );

  // Debounced, and the response is discarded if the address changed while it
  // was in flight -- otherwise a fast typist gets an answer about a name they
  // have already replaced.
  const seq = useRef(0);
  useEffect(() => {
    if (effectiveSlug.length < 2) {
      setAvailability({ checking: false, ok: null, reason: null });
      return;
    }
    const mine = ++seq.current;
    setAvailability({ checking: true, ok: null, reason: null });
    const id = window.setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/signup/availability?slug=${encodeURIComponent(effectiveSlug)}`,
        );
        const j = await r.json();
        if (seq.current === mine) {
          setAvailability({ checking: false, ok: j.available, reason: j.reason ?? null });
        }
      } catch {
        if (seq.current === mine) {
          setAvailability({ checking: false, ok: null, reason: null });
        }
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [effectiveSlug]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_name: venueName.trim(),
          slug: effectiveSlug,
          email: email.trim(),
          display_name: displayName.trim() || null,
          timezone,
          plan,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = body?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail) && detail.length
              ? String(detail[0]?.msg ?? "").replace(/^Value error, /, "")
              : "Something went wrong. Try again.",
        );
        return;
      }
      // Hand over to Stripe. Nothing exists yet on our side but a held address.
      window.location.href = body.checkout_url;
    } catch {
      setError("Can't reach the server right now.");
    } finally {
      setBusy(false);
    }
  };

  const chosen = PLANS.find((p) => p.id === plan)!;
  const canSubmit =
    venueName.trim().length > 0 &&
    effectiveSlug.length >= 2 &&
    email.includes("@") &&
    availability.ok !== false &&
    !busy;

  return (
    <div className="site">
      <div className="site__wrap">
        <Nav page="signup" />

        <section className="site-section" style={{ paddingTop: 48 }}>
          <p className="site-eyebrow">Create your venue</p>
          <h1 className="site-h2">Set up takes about a minute</h1>
          <p className="site-lede">
            Choose your plan and your address, and we will take you to Stripe to
            pay. Your board is live as soon as the payment clears.
          </p>

          <form onSubmit={submit} style={{ maxWidth: 620 }}>
            {error && (
              <div className="site-draft" style={{ marginBottom: 18 }}>
                {error}
              </div>
            )}

            <div className="site-plans" style={{ marginTop: 0, marginBottom: 26 }}>
              {PLANS.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setPlan(p.id)}
                  className={`site-plan${plan === p.id ? " site-plan--selected" : ""}`}
                  style={{ textAlign: "left", cursor: "pointer" }}
                  // A radio group built from buttons still has to tell a screen
                  // reader which one is on.
                  aria-pressed={plan === p.id}
                >
                  {plan === p.id && <span className="site-plan__tag">Selected</span>}
                  <h3>{p.name}</h3>
                  <p className="site-plan__who">{p.who}</p>
                  <div className="site-plan__price">
                    <span className="site-plan__amount">{p.price}</span>
                    <span className="site-plan__per">/ month</span>
                  </div>
                </button>
              ))}
            </div>

            <label className="site-field">
              <span>Venue name</span>
              <input
                className="site-input"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="Paradise Putt Mini Golf"
                required
                maxLength={80}
                autoFocus
              />
            </label>

            <label className="site-field">
              <span>Your address on the web</span>
              <div className="site-slug">
                <span className="site-slug__prefix">minigolfscoreboard.com/tv/</span>
                <input
                  className="site-input site-slug__input"
                  value={effectiveSlug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value.toLowerCase());
                  }}
                  placeholder="paradise-putt"
                  required
                  minLength={2}
                  maxLength={63}
                />
              </div>
              <small className="site-hint">
                {availability.checking
                  ? "Checking…"
                  : availability.ok === false
                    ? availability.reason ?? "That address is taken."
                    : availability.ok === true
                      ? "That address is free."
                      : "This goes on every QR code you print, so it cannot be changed later."}
              </small>
            </label>

            <label className="site-field">
              <span>Your email</span>
              <input
                className="site-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourvenue.com"
                required
              />
              <small className="site-hint">
                We send your sign-in link here. There is no password to choose.
                The link works once and you set one afterwards if you want.
              </small>
            </label>

            <label className="site-field">
              <span>Your name (optional)</span>
              <input
                className="site-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={120}
              />
            </label>

            <button
              type="submit"
              className="site-btn site-btn--primary"
              disabled={!canSubmit}
              style={{ width: "100%", marginTop: 10 }}
            >
              {busy ? "Taking you to Stripe…" : `Continue to payment (${chosen.price}/month)`}
            </button>

            <p className="site-note">
              Payment is handled by Stripe. Card details never touch our servers.
              Cancel any time from your own admin.
            </p>
          </form>
        </section>

        <Footer />
      </div>
    </div>
  );
}

/**
 * Where Stripe sends them back to.
 *
 * Deliberately does not claim the venue is ready. The webhook builds it, and
 * webhooks arrive in their own time -- usually before the redirect lands, but
 * promising something that has not happened yet is how somebody ends up
 * refreshing an empty admin.
 */
export function SignupDone() {
  return (
    <div className="site">
      <div className="site__wrap">
        <Nav page="signup" />
        <section className="site-section" style={{ paddingTop: 64 }}>
          <p className="site-eyebrow">Payment received</p>
          <h2 className="site-h2">Check your email</h2>
          <p className="site-lede">
            Your venue is being set up now. We have sent a sign-in link to the
            address you gave. It works once and lasts 15 minutes.
          </p>
          <p className="site-p">
            If it has not arrived in a few minutes, check the spam folder, then
            get in touch and we will sort it out. Your payment has gone through
            either way.
          </p>
          <div className="site-cta-row" style={{ marginTop: 20 }}>
            <a className="site-btn site-btn--primary" href="/venue">
              Go to sign in
            </a>
          </div>
        </section>
        <Footer />
      </div>
    </div>
  );
}
